<script setup>
import { useI18n } from 'vue-i18n';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import CardTypeIcon from './CardTypeIcon.vue';
import CardPriority from './CardPriority.vue';
import { DEFAULT_FILTERS, PRIORITIES } from '../cardFilters';
import { CARD_STATUSES, statusLabel } from '../cardStatus';

const props = defineProps({
  filters: { type: Object, required: true },
  taskTypes: { type: Array, required: true },
  stages: { type: Array, required: true },
  // The board's names for the card statuses.
  statusLabels: { type: Object, default: () => ({}) },
  // The conversation panel already splits open and closed cards into sections.
  showStatus: { type: Boolean, default: true },
});

const emit = defineEmits(['update']);

const { t } = useI18n();

const ASSIGNEE_LABELS = {
  all: 'CARDS.FILTERS.ASSIGNEE.ALL',
  me: 'CARDS.FILTERS.ASSIGNEE.ME',
  unassigned: 'CARDS.FILTERS.ASSIGNEE.UNASSIGNED',
};

const update = patch => emit('update', { ...props.filters, ...patch });

const toggle = (key, value) => {
  const list = props.filters[key];
  update({
    [key]: list.includes(value)
      ? list.filter(item => item !== value)
      : [...list, value],
  });
};

// The search and the board each have their own control in the header, so they stay.
const clear = () =>
  update({
    ...DEFAULT_FILTERS,
    search: props.filters.search,
    pipelineId: props.filters.pipelineId,
  });

const pillClass = active =>
  active
    ? 'bg-n-brand/10 text-n-blue-11 outline-n-blue-7'
    : 'text-n-slate-11 outline-n-weak hover:bg-n-alpha-2';
</script>

<template>
  <div
    class="absolute z-50 flex flex-col gap-4 p-4 mt-1 border-0 shadow-lg w-80 max-w-[calc(100vw-2rem)] top-full ltr:right-0 rtl:left-0 bg-n-alpha-3 backdrop-blur-[100px] outline outline-1 outline-n-container rounded-xl"
  >
    <section v-if="showStatus" class="flex flex-col gap-2">
      <h4 class="mb-0 text-xs font-medium text-n-slate-11">
        {{ $t('CARDS.FILTERS.STATUS') }}
      </h4>
      <div class="flex gap-1 p-0.5 rounded-lg bg-n-alpha-1">
        <button
          v-for="status in CARD_STATUSES"
          :key="status"
          type="button"
          :data-testid="`filter-status-${status}`"
          class="flex-1 px-2 text-xs truncate rounded-md h-7"
          :class="
            filters.status === status
              ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          @click="update({ status })"
        >
          {{ statusLabel(statusLabels, status, t) }}
        </button>
      </div>
    </section>

    <section class="flex flex-col gap-2">
      <h4 class="mb-0 text-xs font-medium text-n-slate-11">
        {{ $t('CARDS.FILTERS.ASSIGNEE.TITLE') }}
      </h4>
      <div class="flex gap-1 p-0.5 rounded-lg bg-n-alpha-1">
        <button
          v-for="(label, option) in ASSIGNEE_LABELS"
          :key="option"
          type="button"
          :data-testid="`filter-assignee-${option}`"
          class="flex-1 px-2 text-xs truncate rounded-md h-7"
          :class="
            filters.assignee === option
              ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          @click="update({ assignee: option })"
        >
          {{ $t(label) }}
        </button>
      </div>
    </section>

    <section class="flex flex-col gap-1">
      <h4 class="mb-1 text-xs font-medium text-n-slate-11">
        {{ $t('CARDS.FILTERS.TYPE') }}
      </h4>
      <label
        v-for="type in taskTypes"
        :key="type.id"
        class="flex items-center gap-2 px-1 py-1 text-sm rounded-md cursor-pointer text-n-slate-12 hover:bg-n-alpha-1"
        :data-testid="`filter-type-${type.id}`"
      >
        <Checkbox
          :model-value="filters.typeIds.includes(type.id)"
          @change="toggle('typeIds', type.id)"
        />
        <CardTypeIcon :icon="type.icon" />
        <span class="truncate">{{ type.name }}</span>
      </label>
    </section>

    <section class="flex flex-col gap-2">
      <h4 class="mb-0 text-xs font-medium text-n-slate-11">
        {{ $t('CARDS.FILTERS.PRIORITY') }}
      </h4>
      <div class="flex flex-wrap gap-1.5">
        <button
          v-for="priority in PRIORITIES"
          :key="priority"
          type="button"
          :data-testid="`filter-priority-${priority}`"
          class="inline-flex items-center px-2 rounded-lg h-7 outline outline-1"
          :class="pillClass(filters.priorities.includes(priority))"
          @click="toggle('priorities', priority)"
        >
          <CardPriority :priority="priority" show-label />
        </button>
      </div>
    </section>

    <section v-if="stages.length" class="flex flex-col gap-2">
      <h4 class="mb-0 text-xs font-medium text-n-slate-11">
        {{ $t('CARDS.FILTERS.STAGE') }}
      </h4>
      <div class="flex flex-wrap gap-1.5">
        <button
          v-for="stage in stages"
          :key="stage.id"
          type="button"
          :data-testid="`filter-stage-${stage.id}`"
          class="px-2 text-xs rounded-lg h-7 outline outline-1"
          :class="pillClass(filters.stageIds.includes(stage.id))"
          @click="toggle('stageIds', stage.id)"
        >
          {{ stage.name }}
        </button>
      </div>
    </section>

    <label
      class="flex items-center gap-2 text-sm cursor-pointer text-n-slate-12"
      data-testid="filter-overdue"
    >
      <Checkbox
        :model-value="filters.overdue"
        @change="update({ overdue: !filters.overdue })"
      />
      {{ $t('CARDS.FILTERS.OVERDUE') }}
    </label>

    <div class="flex justify-end pt-3 border-t border-n-weak">
      <button
        type="button"
        data-testid="filter-clear"
        class="text-xs text-n-slate-11 hover:text-n-slate-12"
        @click="clear"
      >
        {{ $t('CARDS.FILTERS.CLEAR') }}
      </button>
    </div>
  </div>
</template>
